import { vocativeLt } from '@gramas/shared'
import { Button, cn, useBackLayer, Wordmark } from '@gramas/ui'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useProfile } from '../screens/useProfile.js'
import type { TabKey } from '../shell/TabBar.js'
import {
  CARD_GAP,
  CARD_MIN_PX,
  type CardSide,
  fitsBand,
  padSpot,
  pushDelta,
  rectsAgree,
  roomierSide,
  type SpotRect,
  scrollDelta,
  scrollerOf,
  spotBand,
  TAB_BAR_PX,
} from './geometry.js'
import { TOUR_STEPS } from './steps.js'
import { stopShowingTour } from './store.js'

/** How long the hole takes to travel from one element to the next. */
const TRAVEL_MS = 420

/** Everything outside the hole. Dark enough that the lit thing is the subject. */
const SCRIM = 'rgba(0, 0, 0, 0.74)'

export interface TourProps {
  /** The tab currently painted by the shell. */
  tab: TabKey
  /** Paint a tab without touching the back stack — the tour owns Back itself. */
  onShowTab: (tab: TabKey) => void
  /** Leave, with the app exactly as it was found. */
  onDone: () => void
}

interface Placement {
  spot: SpotRect | null
  side: CardSide
}

/**
 * The welcome walkthrough: a hole in a dark screen, moved from one real control
 * to the next, with a card that says what the lit thing does.
 *
 * The highlight is measured off live DOM rather than described in the step, so
 * a card that moves, a row that grows or a phone that rotates cannot leave the
 * ring pointing at empty background. Nothing is simulated: what is lit is the
 * app itself, still mounted underneath, which is why the steps may only point
 * at elements that are on screen the moment their tab is.
 *
 * Interaction is deliberately blocked while it runs. A tour that lets you tap
 * the thing it is describing is a tour that ends with a half-filled sheet open
 * over step four.
 */
export function Tour({ tab, onShowTab, onDone }: TourProps) {
  const [index, setIndex] = useState(0)
  const [{ spot, side }, setPlacement] = useState<Placement>({ spot: null, side: 'center' })
  /**
   * The screen, as state rather than as a `window` read during render: both the
   * hole and the side the card stands on are derived from it, so a rotation has
   * to re-run the measurement rather than merely re-paint what portrait chose.
   */
  const [viewport, setViewport] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }))
  /**
   * The card's own height, measured rather than assumed. The steps differ by
   * half again in length and the card is nearly half a phone screen, so the
   * space reserved for it has to be the space it actually takes.
   */
  const cardRef = useRef<HTMLDivElement>(null)
  const [cardHeight, setCardHeight] = useState(CARD_MIN_PX)
  const { profile } = useProfile()

  const step = TOUR_STEPS[index]
  const isLast = index === TOUR_STEPS.length - 1

  // Read through a ref: the shell hands this down as an inline function, and
  // depending on it directly would re-measure on every one of its renders.
  const showTab = useRef(onShowTab)
  showTab.current = onShowTab

  /** Where each scroller was before the tour moved it, so it can be put back. */
  const disturbed = useRef(new Map<HTMLElement, number>())

  const close = () => onDone()
  const back = () => setIndex((current) => Math.max(0, current - 1))
  const next = () => {
    if (isLast) {
      onDone()
      return
    }
    setIndex((current) => current + 1)
  }

  /**
   * Back walks the tour before it leaves it. The layer is given back and
   * immediately retaken by the guard, so every press has an entry to spend.
   */
  useBackLayer(true, close, { guard: () => index > 0, onBlocked: back })

  // The app is handed back exactly as it was found, scroll positions included.
  useEffect(() => {
    const moved = disturbed.current
    return () => {
      for (const [scroller, top] of moved) scroller.scrollTop = top
    }
  }, [])

  useEffect(() => {
    const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight })
    window.addEventListener('resize', onResize)
    window.addEventListener('orientationchange', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('orientationchange', onResize)
    }
  }, [])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
      else if (event.key === 'ArrowRight' || event.key === 'Enter') next()
      else if (event.key === 'ArrowLeft') back()
      else return
      event.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  /**
   * Put the hole on this step's element, and the page under it.
   *
   * The tab is switched first and the effect re-entered rather than continued:
   * a pane that is not painted yet is a pane React has not committed, and no
   * amount of waiting inside this pass will produce the element.
   *
   * Then the card claims a side, and the element is scrolled into whatever is
   * left. Reserving in that order is what makes an overlap impossible: with the
   * element centred first, a long step's card simply landed on top of it.
   */
  useEffect(() => {
    if (step == null) return
    if (step.tab != null && step.tab !== tab) {
      showTab.current(step.tab)
      return
    }
    if (step.anchor == null) {
      setPlacement({ spot: null, side: 'center' })
      return
    }

    const selector = `[data-tour="${step.anchor}"]`
    let cancelled = false
    const timers = new Set<ReturnType<typeof setTimeout>>()

    const sleep = (ms: number) =>
      new Promise<void>((resolve) => {
        const id = setTimeout(() => {
          timers.delete(id)
          resolve()
        }, ms)
        timers.add(id)
      })

    const run = async () => {
      let element: HTMLElement | null = null
      for (let attempt = 0; attempt < 12 && !cancelled; attempt++) {
        const found = document.querySelector(selector)
        if (found instanceof HTMLElement) {
          element = found
          break
        }
        await sleep(50)
      }
      if (cancelled) return
      // A step whose anchor never arrives is shown as a plain card rather than
      // as a ring around nothing. Better a missing highlight than a wrong one.
      if (element == null) {
        setPlacement({ spot: null, side: 'center' })
        return
      }

      const scroller = scrollerOf(element)
      if (scroller != null && !disturbed.current.has(scroller)) {
        disturbed.current.set(scroller, scroller.scrollTop)
      }

      /**
       * Jumped, not smooth-scrolled.
       *
       * A smooth scroll is still moving when it returns, and two reads taken
       * before it starts agree with each other — which is how the hole ended up
       * above the row it was lighting, and how it lit the wrong meal entirely.
       * The travel the eye follows is the hole's own; the page beneath a dark
       * scrim may be where it needs to be immediately.
       */
      let chosen: 'top' | 'bottom' = 'bottom'
      let placed = false
      for (const candidate of ['bottom', 'top'] as const) {
        const band = spotBand(candidate, viewport.height, cardHeight)
        if (band.bottom - band.top < element.getBoundingClientRect().height) continue
        if (scroller != null) {
          scroller.scrollTop += scrollDelta(element.getBoundingClientRect(), band)
        }
        if (fitsBand(element.getBoundingClientRect(), band)) {
          chosen = candidate
          placed = true
          break
        }
      }

      // Nothing fitted: an element taller than either strip, or one pinned
      // where it cannot be scrolled away from — the tab bar is both. Whatever
      // can still move is shoved as far from the card as it will go.
      if (!placed) {
        chosen =
          scroller == null
            ? roomierSide(element.getBoundingClientRect(), viewport.height)
            : 'bottom'
        if (scroller != null) {
          const band = spotBand(chosen, viewport.height, cardHeight)
          scroller.scrollTop += pushDelta(element.getBoundingClientRect(), band, 'start')
        }
      }

      const read = (): SpotRect | null => {
        const box = element.getBoundingClientRect()
        if (box.width === 0 || box.height === 0) return null
        return padSpot(box, viewport)
      }

      // A jump lands in one frame, so this settles at once in the ordinary
      // case; it stays for the ones that do not — a list that grew as its live
      // query resolved, a font that swapped, a banner that appeared.
      let previous: SpotRect | null = null
      for (let attempt = 0; attempt < 12 && !cancelled; attempt++) {
        const current = read()
        if (rectsAgree(previous, current)) break
        previous = current
        await sleep(40)
      }
      if (cancelled) return
      setPlacement({ spot: read(), side: chosen })
    }

    void run()

    return () => {
      cancelled = true
      for (const id of timers) clearTimeout(id)
    }
  }, [step, tab, viewport, cardHeight])

  // Before paint, so a card that changed height is never seen in the position
  // the previous step's height chose for it.
  useLayoutEffect(() => {
    const measured = cardRef.current?.getBoundingClientRect().height ?? 0
    if (measured > 0 && Math.abs(measured - cardHeight) > 0.5) setCardHeight(measured)
  })

  if (step == null) return null

  // With no hole the shadow still has to cover the screen, so the box collapses
  // to a point in the middle instead of disappearing — and the next step's
  // spotlight opens out of it rather than cutting in.
  const hole: SpotRect = spot ?? {
    top: viewport.height / 2,
    left: viewport.width / 2,
    width: 0,
    height: 0,
  }
  const name = profile?.displayName?.trim()
  const cameoOk = profile != null && !profile.isAdmin

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Programėlės apžvalga"
      className="fixed inset-0 z-[55] overflow-hidden"
    >
      {/* One element paints the whole scrim: everything outside the hole is its
          own shadow, which is what keeps the dark side seamless while the hole
          travels. Tapping it anywhere moves the tour on. */}
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        onClick={next}
        style={{
          top: hole.top,
          left: hole.left,
          width: hole.width,
          height: hole.height,
          borderRadius: step.shape === 'circle' ? '9999px' : 'var(--radius-card)',
          boxShadow: `0 0 0 9999px ${SCRIM}`,
          transitionDuration: `${TRAVEL_MS}ms`,
        }}
        className="absolute cursor-default transition-[top,left,width,height] ease-[var(--ease-out-quick)]"
      >
        {spot != null && (
          <>
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-[inherit] ring-2 ring-accent"
              style={{ boxShadow: '0 0 26px 6px var(--g-accent-glow)' }}
            />
            {/* The second ring leaves and keeps leaving, so the eye finds the
                highlight even when it landed somewhere it was not looking. */}
            <span
              data-tour-pulse
              aria-hidden="true"
              className="absolute inset-0 rounded-[inherit]"
            />
          </>
        )}
      </button>

      <div
        className={cn(
          'absolute px-3',
          side === 'center' ? 'inset-0 grid place-items-center px-5' : 'inset-x-0',
        )}
        // The safe areas are only readable in CSS, so the side chosen above is
        // turned into a position here rather than in the arithmetic.
        style={
          side === 'top'
            ? { top: `calc(env(safe-area-inset-top) + ${CARD_GAP}px)` }
            : side === 'bottom'
              ? { bottom: `calc(env(safe-area-inset-bottom) + ${TAB_BAR_PX + CARD_GAP}px)` }
              : undefined
        }
      >
        <div
          ref={cardRef}
          // Keyed by step so the card is remounted, and so its entrance plays
          // again, every time the tour moves.
          key={step.id}
          aria-live="polite"
          className={cn(
            'w-full max-w-sm rounded-[var(--radius-card)] border border-line bg-surface p-4',
            'animate-[tour-card-in_220ms_var(--ease-out-quick)]',
            side === 'center' ? 'text-center' : 'mx-auto',
          )}
        >
          {index === 0 && (
            <div className="mb-3 flex flex-col items-center gap-1.5">
              <Wordmark size={26} />
              {name != null && name !== '' && (
                <p className="text-sm text-dim">Labas, {vocativeLt(name)}.</p>
              )}
            </div>
          )}
          {/* Same rule as the sass cameos: the author's own account never
              hears about the author. */}
          {step.cameo != null && cameoOk && (
            <p className="mb-3 text-sm text-accent-text">{step.cameo}</p>
          )}

          <div className="flex items-baseline justify-between gap-3">
            <h2 className="font-display text-lg font-semibold text-ink">{step.title}</h2>
            <span className="tnum shrink-0 text-xs text-mute">
              {index + 1} / {TOUR_STEPS.length}
            </span>
          </div>
          <p className="mt-1.5 text-sm text-dim">{step.body}</p>

          {step.edge != null && (
            <p
              className={cn(
                'mt-2.5 flex gap-2 text-xs text-accent-text',
                side === 'center' && 'text-left',
              )}
            >
              <span
                aria-hidden="true"
                className="mt-[0.3rem] size-1.5 shrink-0 rounded-full bg-accent"
              />
              <span>{step.edge}</span>
            </p>
          )}

          <div aria-hidden="true" className="mt-3.5 flex gap-1">
            {TOUR_STEPS.map((each, i) => (
              <span
                key={each.id}
                className={cn(
                  'h-0.5 flex-1 rounded-full transition-colors duration-[220ms]',
                  i <= index ? 'bg-accent' : 'bg-line',
                )}
              />
            ))}
          </div>

          <div className="mt-3.5 flex gap-2">
            {index > 0 && (
              <Button onClick={back} className="shrink-0">
                Atgal
              </Button>
            )}
            <Button variant="primary" block onClick={next}>
              {isLast ? 'Pradedam' : 'Toliau'}
            </Button>
          </div>

          {/* Both exits on one line: leaving this once and leaving it for good
              are different decisions, and neither is worth a row of its own. */}
          <div className="mt-3 flex items-baseline justify-between gap-3 text-xs text-mute">
            <button
              type="button"
              onClick={close}
              className="underline underline-offset-2 hover:text-ink"
            >
              Praleisti
            </button>
            <button
              type="button"
              onClick={() => {
                void stopShowingTour()
                onDone()
              }}
              className="underline underline-offset-2 hover:text-ink"
            >
              Daugiau neberodyti
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
